import React, { LazyExoticComponent, ComponentType, lazy } from 'react';

// Default locale
export const DEFAULT_LOCALE = 'en';
export type Locale = string; // Use string type to accommodate any language code from i18n

// Routes without locale prefixes
export const ROUTES = {
  ROOT: '/',
  DISCOVER: '/discover',
  DASHBOARD: '/dashboard',
  BOT_SYSTEM: '/bot',
  BOT_DETAIL: '/bot/:id',
  BOT_CREATE: '/bot/create',
  BOT_FLOW_EDITOR: '/bot/flow-editor',
  BOT_FLOW_PREVIEW: '/bot/flow-preview',
  SETTINGS: '/settings',
  PRICING: '/pricing',
  PRIVACY_POLICY: '/privacy-policy',
  TERMS_OF_SERVICE: '/terms-of-service',
  PAYMENT: '/payment',
  PAYMENT_HISTORY: '/payment/history',
  PAYMENT_RESULT: '/payment/result',
  CREDIT_MANAGEMENT: '/credits',
  CHANNELS: '/channels',
  CHANNEL_DETAIL: '/channels/:id',
  ALERT_HISTORY: '/alerts',
  ALERT_DETAIL: '/alerts/detail',
  STRATEGY_DETAIL: '/strategy/detail',
  USER_DETAIL: '/user/:id',
  NETWORK: '/network',
  NOT_FOUND: '*',
} as const;

export interface RouteConfig {
  path: string;
  element?: JSX.Element | null;
  elementComponent?: LazyExoticComponent<ComponentType<Record<string, unknown>>>;
  requireAuth?: boolean;
  title: string;
  description?: string;
  icon?: string;
  children?: RouteConfig[];
}

// Lazy-loaded components - will be imported when needed
const Discover = lazy(() => import('@/pages/Discover'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
// Add error handling for BotSystem import
const BotSystem = lazy(() =>
  import('@/pages/BotSystem').catch((error) => {
    console.error('Error loading BotSystem component:', error);
    return {
      default: () =>
        React.createElement(
          'div',
          null,
          'Error loading Bot System. Please try again later.',
        ),
    };
  }),
);
const Settings = lazy(() => import('@/pages/Settings'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const Payment = lazy(() => import('@/pages/Payment'));
const PaymentResult = lazy(() => import('@/pages/PaymentResult'));
const PaymentHistory = lazy(() => import('@/pages/PaymentHistory'));
const CreditManagement = lazy(() => import('@/pages/CreditManagement'));
const Channels = lazy(() => import('@/pages/Channels'));
const ChannelDetail = lazy(() => import('@/pages/ChannelDetail'));
const BotFlowEditor = lazy(() => import('@/pages/BotFlowEditor'));
const BotFlowPreview = lazy(() => import('@/pages/BotFlowPreview'));
const AlertHistory = lazy(() => import('@/pages/AlertHistory'));
const AlertDetail = lazy(() => import('@/pages/AlertDetail'));
const StrategyDetail = lazy(() => import('@/pages/StrategyDetail'));
const UserDetail = lazy(() => import('@/pages/UserDetail'));
const Network = lazy(() => import('@/pages/Network'));
const NotFound = lazy(() => import('@/pages/NotFound'));

export const ROUTE_CONFIG: RouteConfig[] = [
  // ── Primary navigation ────────────────────────────────────────────────
  {
    path: ROUTES.DASHBOARD,
    elementComponent: Dashboard,
    requireAuth: true,
    title: 'Dashboard',
    description: 'Overview of your strategies, alerts, channels, and credits',
    icon: 'dashboard',
  },
  {
    path: ROUTES.DISCOVER,
    elementComponent: Discover,
    requireAuth: false,
    title: 'Discover',
    description: 'Discover and subscribe to trading strategies',
    icon: 'explore',
  },
  {
    path: ROUTES.BOT_SYSTEM,
    elementComponent: BotSystem,
    requireAuth: true,
    title: 'Strategy Management',
    description: 'Create and manage your AI strategies',
    icon: 'smart_toy',
    children: [
      {
        path: ROUTES.BOT_CREATE,
        title: 'Create Strategy',
        description: 'Create a new AI strategy',
      },
      {
        path: ROUTES.BOT_DETAIL,
        title: 'Strategy Details',
        description: 'View and edit strategy details',
      },
    ],
  },
  {
    path: ROUTES.ALERT_HISTORY,
    elementComponent: AlertHistory,
    requireAuth: true,
    title: 'Alert History',
    description: 'View alert delivery history',
    icon: 'notifications',
    children: [
      {
        path: ROUTES.ALERT_DETAIL,
        title: 'Alert Detail',
        description: 'View a single alert delivery detail',
      },
    ],
  },
  {
    path: ROUTES.ALERT_DETAIL,
    elementComponent: AlertDetail,
    requireAuth: true,
    title: 'Alert Detail',
    description: 'View a single alert delivery detail',
  },
  {
    path: ROUTES.PRICING,
    elementComponent: Pricing,
    requireAuth: false,
    title: 'Pricing',
    description: 'Browse and purchase credit packages',
    icon: 'diamond',
  },

  // ── Bot flow routes (full-width, no sidebar) ──────────────────────────
  {
    path: ROUTES.BOT_FLOW_EDITOR,
    elementComponent: BotFlowEditor,
    requireAuth: true,
    title: 'Strategy Flow Editor',
    description: 'Design and manage strategy conversation flows',
    icon: 'account_tree',
  },
  {
    path: ROUTES.BOT_FLOW_PREVIEW,
    elementComponent: BotFlowPreview,
    requireAuth: true,
    title: 'Strategy Flow Preview',
    description: 'Read-only flow viewer for submitted strategies',
    icon: 'visibility',
  },

  // ── Account & billing (sidebar sub-menu) ──────────────────────────────
  {
    path: ROUTES.CHANNELS,
    elementComponent: Channels,
    requireAuth: true,
    title: 'Channels',
    description: 'Create and manage notification channels',
    icon: 'send',
    children: [
      {
        path: ROUTES.CHANNEL_DETAIL,
        title: 'Channel Details',
        description: 'View notification channel details and strategy usage',
      },
    ],
  },
  {
    path: ROUTES.CHANNEL_DETAIL,
    elementComponent: ChannelDetail,
    requireAuth: true,
    title: 'Channel Details',
    description: 'View notification channel details and strategy usage',
  },
  {
    path: ROUTES.CREDIT_MANAGEMENT,
    elementComponent: CreditManagement,
    requireAuth: true,
    title: 'Credit Management',
    description: 'View and manage your credit balances',
    icon: 'account_balance_wallet',
  },
  {
    path: ROUTES.PAYMENT_HISTORY,
    elementComponent: PaymentHistory,
    requireAuth: true,
    title: 'Payment History',
    description: 'View payment transaction history',
    icon: 'history',
  },
  {
    path: ROUTES.SETTINGS,
    elementComponent: Settings,
    requireAuth: true,
    title: 'Settings',
    description: 'Configure system settings',
    icon: 'settings',
  },

  // ── Payment flow (not in sidebar) ─────────────────────────────────────
  {
    path: ROUTES.PAYMENT,
    elementComponent: Payment,
    requireAuth: true,
    title: 'Payment',
    description: 'Payment checkout page',
    icon: 'payment',
  },
  {
    path: ROUTES.PAYMENT_RESULT,
    elementComponent: PaymentResult,
    requireAuth: true,
    title: 'Payment Result',
    description: 'Payment result confirmation page',
    icon: 'receipt',
  },

  // ── Strategy detail (public-facing) ────────────────────────────────────
  {
    path: ROUTES.STRATEGY_DETAIL,
    elementComponent: StrategyDetail,
    requireAuth: false,
    title: 'Strategy Detail',
    description: 'View strategy details and subscribe',
    icon: 'analytics',
  },

  // ── My network (signed-in only) ────────────────────────────────────────
  // requireAuth:true — every endpoint behind it (/user/following, /user/followers)
  // describes the *authenticated* user's relationships and takes no user id.
  {
    path: ROUTES.NETWORK,
    elementComponent: Network,
    requireAuth: true,
    title: 'My Network',
    description: 'People you follow and who follow you',
    icon: 'group',
  },

  // ── User profile (public-facing) ───────────────────────────────────────
  // requireAuth:false — GET /public/users/{id} is auth-optional and returns the
  // public subset (or the minimal private shape) to anonymous callers.
  {
    path: ROUTES.USER_DETAIL,
    elementComponent: UserDetail,
    requireAuth: false,
    title: 'User Profile',
    description: 'View a user profile',
    icon: 'person',
  },

  // Public About Us pages
  {
    path: ROUTES.PRIVACY_POLICY,
    elementComponent: PrivacyPolicy,
    requireAuth: false,
    title: 'Privacy Policy',
    description: 'How we collect, use, and protect personal data',
  },
  {
    path: ROUTES.TERMS_OF_SERVICE,
    elementComponent: TermsOfService,
    requireAuth: false,
    title: 'Terms of Service',
    description: 'Terms governing the use of Deview services',
  },

  // ── Catch-all ─────────────────────────────────────────────────────────
  {
    path: ROUTES.NOT_FOUND,
    elementComponent: NotFound,
    title: 'Not Found',
    description: 'The requested page could not be found',
  },
];
